import React, { useCallback, useState, useEffect, useRef } from 'react';
import useColumnWidthsChange from 'src/componentes/list/utils/use-column-widths-change';
import { useIntl } from 'react-intl';
import ListColumn from 'src/componentes/NewList/ListColumn';
import Toolbar from 'src/componentes/NewList/Toolbar';
import Results from 'src/componentes/NewList/Results';
import Button from 'src/componentes/button';
import LoadingContainer from 'src/componentes/loading-container';
import {
  getColumns,
  getFilter,
  getData,
  getPermissions,
} from 'src/componentes/NewList';
import LucideIcon from 'src/componentes/lucide-icon';
import { FILTROS, COLUNAS } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/error-handler';
import useAuth from 'src/hooks/useAuth';
import ContentManager from 'src/componentes/content-manager';
import { format } from 'date-fns';
import formatDateFns from 'src/utils/formatDateFns';
import Edit from '../MapaSucessaoEditView';

function MapaSucessaoListView({ defaultSort, defaultSortBy, parameters }) {
  const columnsTag = COLUNAS.MAPA_SUCESSAO;
  const filterTag = FILTROS.MAPA_SUCESSAO_LIST;
  const endpoint = '/MapaSucessao';
  const openLastFilter = false;
  const showToolbar = true;
  const selectedFilter = {};
  const entityTag = null;

  const intl = useIntl();
  const { terms: resources, user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const handleColumnWidthsChange = useColumnWidthsChange(
    setColumns,
    setAllColumns
  );
  const [isLoading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(defaultSort);
  const [sortFieldBy, setSortFieldBy] = useState(defaultSortBy);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const defaultColumnsRef = useRef([]);

  const loadColumns = async () => {
    try {
      const currentColumns = await getColumns(
        defaultColumnsRef.current,
        entityTag,
        columnsTag
      );
      setColumns(currentColumns.columns);
      setAllColumns(currentColumns.allColumns);
    } catch (err) {
      errorHandler(err);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const newPermissions = await getPermissions(endpoint, parameters);

      let newData = [];

      if (newPermissions.allowView || newPermissions.allowList) {
        await loadColumns();

        let newFilter = selectedFilter;
        if (openLastFilter && filterTag)
          newFilter = await getFilter(filterTag, true);

        let valueFilter = {};
        if (newFilter != null) valueFilter = newFilter.valor;

        const model = { ...valueFilter, searchText };
        newData = await getData(
          endpoint,
          model,
          sortField,
          sortFieldBy,
          pageSize,
          currentPage - 1,
          parameters
        );
        setData(newData.list);
        setCount(newData.count);
      }
      setLoading(false);
      return newData;
    } catch (err) {
      setLoading(false);
      errorHandler(err);
    }
  }, [
    endpoint,
    parameters,
    searchText,
    pageSize,
    currentPage,
    sortField,
    sortFieldBy,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, pageSize, currentPage, searchText, sortField, sortFieldBy]);

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  });

  const handleColumnsChange = useCallback(async () => {
    await loadColumns();
    await loadData();
  });

  const handleRefresh = useCallback(async () => {
    await loadData();
  });

  const handleChangeCurrentPage = useCallback((newValue) => {
    setCurrentPage(newValue);
  });

  const handleChangePageSize = useCallback((pageSize) => {
    setPageSize(pageSize);
  });

  const handleSortColumn = useCallback((sort) => {
    setSortField(sort);
  });

  const handleSortByColumn = useCallback((sortBy) => {
    setSortFieldBy(sortBy);
  });

  const renderDate = (date) =>
    format(date, currentDisplay, { locale: currentLocale });

  const renderColaborador = (item) => item?.colaborador?.nome;

  const defaultColumns = [
    <ListColumn
      key="colaborador"
      headerText={capitalizeFirstLetter(
        intl.formatMessage(
          { id: 'colaborador' },
          { colaborador: resources.colaborador }
        )
      )}
      valueField="colaborador"
      default
      sortField="avaliado.nome"
      valueFunction={renderColaborador}
      required
    />,
    <ListColumn
      key="area"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'area' }, { area: resources.area })
      )}
      valueField="area"
      default
      sortField="organizacaoColaborador.nome"
      valueFunction={(item) => item?.area?.nome}
    />,
    <ListColumn
      key="tempoSucessao"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'tempoParaSuceder' })
      )}
      default
      valueField="tempoSucessao"
      sortField="tempoSucessao.nome"
      valueFunction={(item) => item?.tempoSucessao?.nome}
    />,
    <ListColumn
      key="indicadoPor"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'indicadoPor' })
      )}
      default
      valueField="indicadoPor"
      sortField="indicador.nome"
      valueFunction={(item) => item?.indicador?.nome}
    />,
    <ListColumn
      key="origemIndicacao"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'origemIndicacao' })
      )}
      default
      valueField="origemIndicacao"
      sortField="origemIndicacao"
    />,
    <ListColumn
      key="justificativa"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'justificativa' })
      )}
      default
      valueField="justificativa"
      sortField="justificativa"
    />,
    <ListColumn
      key="dataIndicacao"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'labelDataIndicacao' })
      )}
      default
      valueField="dataIndicacao"
      sortField="dataIndicacao"
      valueFunction={(item) =>
        item?.dataIndicacao && renderDate(item.dataIndicacao)
      }
    />,
  ];
  defaultColumnsRef.current = defaultColumns;

  const handleNew = () =>
    ContentManager.addContent(
      <Edit handleClose={handleRefresh} parameters={parameters} />
    );

  const handleEdit = (item) =>
    ContentManager.addContent(<Edit item={item} handleClose={handleRefresh} />);

  const actions = [
    <Button key={1} className="ml-2" onClick={handleNew} type="primary">
      <LucideIcon icon="plus" />
      <span className="ml-2">
        {capitalizeFirstLetter(intl.formatMessage({ id: 'label.novo' }))}
      </span>
    </Button>,
  ];

  return (
    <LoadingContainer isLoading={isLoading}>
      {showToolbar && (
        <Toolbar
          showSearch
          onColumnsChange={handleColumnsChange}
          onSearchChange={handleSearchChange}
          onRefresh={handleRefresh}
          searchText={searchText}
          columnsTag={columnsTag}
          columns={columns}
          allColumns={allColumns}
          actions={actions}
          isLoading={isLoading}
        />
      )}
      <Results
        columnsTag={columnsTag}
        onColumnsChange={handleColumnWidthsChange}
        resources={resources}
        url={endpoint}
        count={count}
        columns={columns}
        data={data}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangeCurrentPage={handleChangeCurrentPage}
        onChangePageSize={handleChangePageSize}
        onSortColumn={handleSortColumn}
        onSortByColumn={handleSortByColumn}
        sort={sortField}
        sortBy={sortFieldBy}
        updatedItems={[]}
        showMenu
        showDelete={true}
        onRefresh={handleRefresh}
        handleEditClick={handleEdit}
        handleRefreshPage={handleRefresh}
      />
    </LoadingContainer>
  );
}

MapaSucessaoListView.defaultProps = {
  parameters: {},
};

export default MapaSucessaoListView;
